import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';

import RefreshButton from 'components/RefreshButton';
import { useCustomEvent } from 'components/ApplicationInsights';
import { Visibility } from 'components/Visibility';

import iconOrderActiveDesc from 'img/icon-order-active-desc.svg';
import iconOrderActiveAsc from 'img/icon-order-active-asc.svg';
import iconOrderInactive from 'img/icon-order-inactive.svg';
import IconArrowUp from 'img/arrow-up.svg';
import IconArrowDown from 'img/arrow-down.svg';

import { EnumStatusDisabledArrowDown, ITableModel, TableHealthOperatorProps } from './interface';

import { TableItem } from 'components/Table/TableItem';
import { Column, Row } from 'components/Layout/Grid';
import { Button } from 'components/Button';
import Text from 'components/Text';
import Loading from 'components/Loading';
import Paginator from 'components/Paginator';

import '../../Table/styles.scss';

const TableHealthOperator: React.FC<TableHealthOperatorProps> = (props) => {

  const {
    className,
    pageSize,
    showPagination,
    data,
    model,
    currentPage,
    totalPages,
    total,
    DropdownTemplate,
    error,
    onReload,
    setTableOrder,
    tableOrder,
    onPageChange,
    customProps,
    trackingKey,
  } = props;

  const trackOrderBy = useCustomEvent(trackingKey ?? '', 'Ordenar');

  const [hideShowTooltip, setHideShowTooltip] = useState(false);

  const [expandedDropdown, setExpandedDropdown] = useState<number | undefined>(undefined);

  const isEven = (el: number) => (el % 2 !== 0 ? 'bg-white' : 'bg-gray-10');

  const orderToOData = (str?: string) =>
    str?.split(' ')[0].replace('.', '/') || str;
  const isOrderAsc = (str: string) => str.split(' ')[1] === 'asc';
  const isOrderDesc = (str: string) => str.split(' ')[1] === 'desc';
  const orderToAsc = (str?: string) => `${str} asc` || str;
  const orderToDesc = (str?: string) => `${str} desc` || str;

  const wrapperRef = useRef(null);
  useMouseMove(wrapperRef);

  useEffect(() => {
    setExpandedDropdown(undefined);
  }, [data]);

  const colorRowTable = useCallback((index: number) => {
    return isEven(index);
  }, [data]);


  function useMouseMove(ref: any) {
    useEffect(() => {
      let hideShowTooltipInside = false;
      function handleMoveMouse(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setHideShowTooltip(true);
          hideShowTooltipInside = true;
        }
      }

      !hideShowTooltipInside && document.addEventListener("mouseover", handleMoveMouse);
      return () => {
        !hideShowTooltipInside && document.removeEventListener("mouseover", handleMoveMouse);
      };
    }, [ref]);
  }

  function expandedDropdownAndSendDataLayer(propsParam: any) {
    setExpandedDropdown((old) =>
      old !== propsParam.index ? propsParam.index : undefined
    );
  }

  const disableArrowDown = (propsParam: any): boolean => {
    return propsParam.data.statusFlow !== EnumStatusDisabledArrowDown.FINISHED || !!propsParam.data.isMultipleInvoice;
  };

  const Dropdown = {
    Space: () => {
      if (DropdownTemplate) {
        return (
          <Row
            className="align-center bg-gray-20 p-2 flex-1 h-100"
            style={{
              minWidth: 50,
              maxWidth: 50,
              width: 50,
            }}
          />
        );
      }

      return null;
    },

    Arrow: (propsParam: any) => {
      if (DropdownTemplate) {
        return (
          <Row
            className={`align-center p-2 flex-1 ${colorRowTable(propsParam.index)}`}
            style={{
              minWidth: 50,
              maxWidth: 50,
              width: 50,
              height: 50,
            }}
          >
            <Button
              key={propsParam.index}
              onClick={() => expandedDropdownAndSendDataLayer(propsParam)}
              className="no-style"
            >
              <Visibility visible={disableArrowDown(propsParam)}>
                <Visibility visible={expandedDropdown === propsParam.index}>
                  <img
                    src={IconArrowUp}
                    alt=""
                    style={{
                      transition: 'all .3s ease',
                      transform: 'rotate(180deg)',
                    }}
                  />
                </Visibility>
                <Visibility visible={expandedDropdown !== propsParam.index}>
                  <img
                    src={IconArrowDown}
                    alt=""
                    style={{
                      transition: 'all .3s ease',
                      transform: 'rotate(0deg)',
                    }}
                  />
                </Visibility>
              </Visibility>
            </Button>
          </Row >
        );
      }
      return null;
    },
  };

  const handleClickTableLabel = (field: ITableModel) => {
    if (!field.noFilter) {
      if (props.trackingKey) trackOrderBy({ item: trackingKey, chave: field.key });
      setTableOrder((order: any) => sortOrderTable(field, order));
    }
  };

  const sortOrderTable = (field: ITableModel, sort: string) => {
    const isFieldKey = typeof field.key !== 'undefined';

    if (isFieldKey && orderToOData(sort) !== orderToOData(field.key))
      return orderToAsc(orderToOData(field.key));

    if (isFieldKey && orderToOData(sort) === orderToOData(field.key) &&
      isOrderAsc(sort))
      return orderToDesc(orderToOData(field.key));

    return isFieldKey && orderToOData(sort) === orderToOData(field.key) &&
      isOrderDesc(sort) ? orderToAsc(orderToOData(field.key)) : '';
  };

  const iconSortOrderTable = (field: ITableModel) => {
    const isOrderToOData = orderToOData(tableOrder) === orderToOData(field.key);

    if (isOrderToOData && isOrderAsc(tableOrder))
      return iconOrderActiveAsc;

    return isOrderToOData && isOrderDesc(tableOrder) ?
      iconOrderActiveDesc : iconOrderInactive;
  };

  const labelHeight = 50;

  const TableLabel = useMemo(
    () =>
      model.map((m: any, _: any) => (
        <Row
          key={_}
          className={`align-center bg-gray-20 ${m.isButton ? 'px-1' : 'py-2 px-2'
            } flex-1`}
          style={{
            minHeight: labelHeight,
            height: labelHeight,
            maxHeight: labelHeight,
            minWidth: m.size || 100,
            maxWidth: m.maxSize || 'auto',
          }}
        >
          {m.label && (
            <Button
              onClick={() => handleClickTableLabel(m)}
              className={`d-flex align-center ${m.isButton ? 'px-1' : 'p-0'} flex-wrap`}
            >
              <Row>
                <Text
                  size={12}
                  className={`text-left color-primary font-bold text-wrap`}
                >
                  {m.label}
                </Text>
                <Visibility visible={!m.noFilter}>
                  <img
                    className="ml-1"
                    src={iconSortOrderTable(m)}
                    alt=""
                  />
                </Visibility>
              </Row>
            </Button>
          )}
        </Row>
      )),
    [data, model]
  );

  const TableRow = (propsRow: any) =>
    useMemo(
      () => (
        <Row
          className={`align-center ${propsRow.modelItem.isButton ? 'px-1' : 'py-2 px-2'
            } flex-1 h-100 ${colorRowTable(propsRow.objectIndex)}
            ${propsRow.modelItem.isButton ? 'disableIcons' : ''}`}
          style={{
            minWidth: propsRow.modelItem.size || 100,
            maxWidth: propsRow.modelItem.maxSize || 'auto',
            height: 50,
          }}
        >
          {propsRow.modelItem.key === 'item' &&
            <Text className="number-order-ranking">{propsRow.objectIndex + 1}
            </Text>
          }
          {propsRow.children}
        </Row>
      ),
      [propsRow]
    );

  const MemoRow = (propsMemoRow: any) =>
    useMemo(() => {
      const { item } = propsMemoRow;

      return (
        <Column
          className="relative"
          style={Object.assign(
            { display: 'grid' }
          )}
          key={propsMemoRow.i}
        >
          {
            <Row
              className={`align-center min-w-100 ${'item-ranking-table'}`}
              style={{ height: 50 }}
            >

              <Dropdown.Arrow index={propsMemoRow.i} data={item} />

              {model.map((m: any, _: any) => (
                <TableRow
                  key={_}
                  modelItem={m}
                  keyIndex={_}
                  objectIndex={propsMemoRow.i}
                >
                  <TableItem
                    trackingKeyProps={propsMemoRow.trackingKey}
                    item={propsMemoRow.item}
                    modelItem={m}
                    customProps={customProps}
                  />
                </TableRow>
              ))}
            </Row>
          }

          {expandedDropdown === propsMemoRow.i && DropdownTemplate && (
            <DropdownTemplate {...{ ...propsMemoRow.item, ...customProps }} />
          )}
        </Column>
      );
    }, [propsMemoRow]);

  const valueMinHeightBottomBtn = 0;

  return (
    <>
      <Visibility visible={typeof data != 'undefined'}>
        <Row>
          <Text className='align-center color-primary mb-2'>
            Foram encontrados <span className='font-bold'>{`${total} resultados`}</span> para este período
          </Text>
        </Row>
      </Visibility>
      <Column
        className={`relative flex-1 overflow-x-hidden ${className}` || ''}
        style={{
          minHeight:
            typeof data === 'undefined'
              ? 50 * (pageSize || 10) + 50 + (valueMinHeightBottomBtn)
              : 'auto',
          borderRadius: '10px 10px 0 0',
        }}
      >
        <Visibility visible={typeof data === 'undefined'}>
          <>
            <Visibility visible={!error}>
              <Loading size={45} />
            </Visibility>

            <Visibility visible={!!error}>
              <RefreshButton onReload={onReload} />
            </Visibility>
          </>
        </Visibility>

        <Column className="relative flex-1 overflow-x-auto">
          <Row
            className={`align-center ${'label-title-ranking'}`}
            style={{
              minHeight: labelHeight,
              height: labelHeight,
              maxHeight: labelHeight,
            }}
          >
            <Dropdown.Space />
            {TableLabel}
          </Row>

          {data && data?.map(
            (item: any, i: number) =>
              i < (pageSize || i + 1) && (
                <MemoRow
                  key={i}
                  trackingKeyProps={props.trackingKey}
                  item={item}
                  i={i}
                />
              )
          )}

          {hideShowTooltip && <Tooltip variant="light" />}
        </Column>
      </Column>

      <Row className="align-center ajust-footer-elements customer justify-center">
        <Column>
          {pageSize &&
            typeof data !== 'undefined' &&
            totalPages &&
            (showPagination == null || showPagination === true) &&
            totalPages > 1 ? (
            <Paginator
              pageSize={pageSize}
              currentPage={currentPage}
              totalPage={totalPages}
              onPageChange={onPageChange}
            />
          ) : null}
        </Column>
      </Row>
    </>
  );
};

export default TableHealthOperator;
